/**
 * UTM passthrough
 * -----------------------------------------------------------------------------
 * Decorates outbound (off-site) links so the destination property — HubSpot,
 * accessibility.deque.com, app.deque.com, etc. — can attribute which deque.com
 * page sent the user.
 *
 * Behavior:
 *   1. Any UTM params present in the CURRENT page URL are carried through to
 *      links that point AWAY from this site (a different host).
 *   2. The current page slug is written to `utm_content` (NOT `utm_campaign`).
 *      Page-level attribution lives in utm_content so HubSpot campaign reporting
 *      stays clean. A utm_content that arrives inbound, or is hard-coded on the
 *      link, is left untouched (inbound/explicit wins).
 *
 * Scope: every off-site link on the page (per the requirement to tag all links
 * pointing away from www.deque.com). Same-site / relative / mailto / tel /
 * anchor links are skipped.
 *
 * Why client-side: deque.com is page-cached on Pantheon. Injecting a visitor's
 * inbound UTMs server-side would bake one visitor's params into the cached HTML
 * served to everyone else. Reading the live URL in the browser avoids that.
 *
 * Kill switch: set window.dequeUTM.enabled = false (PHP filter
 * `deque_utm_passthrough_enabled`) to disable site-wide or per-page.
 */

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

// Treat the apex and the www host as the same site.
function normalizeHost(host) {
	return host.replace(/^www\./i, '').toLowerCase();
}

// True only for http(s) links whose host differs from the current page's host.
function isOffsite(anchor) {
	if (anchor.protocol !== 'http:' && anchor.protocol !== 'https:') return false;
	if (!anchor.host) return false;
	return normalizeHost(anchor.host) !== normalizeHost(window.location.host);
}

// UTM params present on the current page URL.
function getInboundUtms() {
	const current = new URLSearchParams(window.location.search);
	const inbound = {};
	UTM_KEYS.forEach((key) => {
		const value = current.get(key);
		if (value) inbound[key] = value;
	});
	return inbound;
}

// Page slug for utm_content. Prefer the server-provided value (true post_name,
// '404' on not-found pages); fall back to the last path segment.
function getPageSlug() {
	if (window.dequeUTM && window.dequeUTM.slug) return window.dequeUTM.slug;
	const segments = window.location.pathname.split('/').filter(Boolean);
	return segments.length ? segments[segments.length - 1] : 'home';
}

function decorate(anchor, inbound, slug) {
	let url;
	try {
		url = new URL(anchor.href);
	} catch (e) {
		return; // malformed href — leave it alone
	}

	const params = url.searchParams;

	// Carry inbound UTMs through without clobbering params the link already sets.
	Object.keys(inbound).forEach((key) => {
		if (!params.has(key)) params.set(key, inbound[key]);
	});

	// Page attribution. Inbound / explicit utm_content wins; otherwise the slug.
	if (slug && !params.has('utm_content')) {
		params.set('utm_content', slug);
	}

	anchor.href = url.toString();
}

function runUTMPassthrough() {
	if (window.dequeUTM && window.dequeUTM.enabled === false) return;
	const inbound = getInboundUtms();
	const slug = getPageSlug();
	document.querySelectorAll('a[href]').forEach((anchor) => {
		if (isOffsite(anchor)) decorate(anchor, inbound, slug);
	});
}

document.addEventListener('DOMContentLoaded', () => {
	// Defer until after Weglot's JS has translated links, so UTMs are appended
	// to the already-translated href rather than the original.
	if (window.Weglot) {
		window.Weglot.on('initialized', runUTMPassthrough);
	} else {
		runUTMPassthrough();
	}
});
